import { useHistory, useSelectedPathIds, useSitemap, useSitemapItems } from '@msdk/common'
import { AreaGroup, Sitemap } from '@msdk/definitions'
import { List } from '@msdk/ui'
import { styled } from '@msdk/ui'
import { SidebarDivider } from 'components/SidebarDivider'
import { DocumentDownloadSection } from 'components/SideMenu/DocumentDownloadSection'
import { DocumentSection } from 'components/SideMenu/DocumentSection'
import { useEffect, useState } from 'react'
import { useNavigationMenu } from 'state/navigation-menu'

import { ConditionalSitemapRenderer } from './ConditionalSitemapRenderer'
import { SidebarList } from './SidebarList'
import { findAreaById, findParentAreaId, findSelectedIds, isAreaOpen, SelectedPathIdsAreDifferent } from './utils'

const Container = styled('div')`
  display: flex;
  flex-direction: column;
`

export const KinexusContainer = styled(List)`
  width: 100%;
  padding: 0 12px;
  display: flex;
  flex-flow: column wrap;
  margin: 0 0 20px;
  gap: 0;
`

export const SidebarDividerForNonKinexus = styled(SidebarDivider)`
  margin-top: 12px;
`

interface Props {
  sitemapModel: Sitemap
}

export function Sidebar({ sitemapModel }: Props) {
  const defaultSitemap = useSitemap('DefaultArea')
  const sitemapItems = useSitemapItems()

  const [selectedPathIds, setSelectedPath] = useSelectedPathIds()
  const [sitemapModelAreas, setSitemapModelAreas] = useState<AreaGroup[]>([])
  const [returnToKinexusItem, setReturnToKinexusItem] = useState<AreaGroup>()
  const history = useHistory()

  const current = history.current()

  const isKinexusSitemap = sitemapModel.name === 'WebKinexusArea'

  useEffect(() => {
    const selectedIds = findSelectedIds(
      sitemapModel.areas,
      defaultSitemap.areas,
      sitemapItems,
      current?.params['entityName'],
      current?.params['moduleName'],
      current?.isCustom,
    )

    if (!isKinexusSitemap) {
      setSitemapModelAreas(sitemapModel.areas)
    } else {
      const kinexusArea = sitemapModel.areas.filter((area) => area.id !== 'KSA_Return_To_Kinexus_Area')
      const kinexusReturnArea = sitemapModel.areas.find((area) => area.id === 'KSA_Return_To_Kinexus_Area')
      setSitemapModelAreas(kinexusArea)
      setReturnToKinexusItem(kinexusReturnArea)
    }

    if (!!selectedIds && SelectedPathIdsAreDifferent(selectedPathIds, selectedIds)) {
      setSelectedPath(selectedIds)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sitemapModel, defaultSitemap, current?.params])

  const { navigationMenuOpen, setNavigationMenu } = useNavigationMenu()

  const className = navigationMenuOpen ? 'horizontal-margin' : undefined

  const [currentExpandedSitemap, setCurrentExpandedSitemap] = useState<Sitemap>()
  const [currentExpandedAreaId, setCurrentExpandedAreaId] = useState<string>()

  const toggleExpand = (sitemap: Sitemap, areaId: string) => {
    if (!navigationMenuOpen) {
      setNavigationMenu(navigationMenuOpen)
      setCurrentExpandedAreaId(areaId)
      return
    }

    if (currentExpandedAreaId === areaId) {
      setCurrentExpandedAreaId(findParentAreaId(areaId, sitemap.areas, undefined))
      return
    }

    const area = findAreaById(areaId, sitemap.areas)
    if (area && isAreaOpen(area, currentExpandedAreaId)) {
      setCurrentExpandedAreaId(findParentAreaId(area.id, sitemap.areas, undefined))
      return
    }

    setCurrentExpandedAreaId(areaId)
  }

  const shouldShowDefaultSitemap = sitemapModel.name !== defaultSitemap.name && defaultSitemap.areas.length > 0

  return (
    <Container>
      {returnToKinexusItem && (
        <>
          <SidebarList component="nav">
            <ConditionalSitemapRenderer
              last={true}
              key={returnToKinexusItem.id}
              type={returnToKinexusItem.type}
              item={returnToKinexusItem}
              mode="Extended"
              selectedPathIds={selectedPathIds}
              level={0}
              currentExpanded={
                navigationMenuOpen && currentExpandedSitemap === sitemapModel ? currentExpandedAreaId : undefined
              }
              onExpand={(id) => {
                setCurrentExpandedSitemap(sitemapModel)
                toggleExpand(sitemapModel, id)
              }}
            />
          </SidebarList>
          <SidebarDivider className={className} />
        </>
      )}
      {sitemapModelAreas.length > 0 && (
        <>
          <SidebarList component="nav">
            {sitemapModelAreas.map((area, i) => (
              <ConditionalSitemapRenderer
                last={shouldShowDefaultSitemap || isKinexusSitemap ? i === sitemapModelAreas.length - 1 : false}
                key={area.id}
                type={area.type}
                item={area}
                mode="Extended"
                selectedPathIds={selectedPathIds}
                level={0}
                currentExpanded={
                  navigationMenuOpen && currentExpandedSitemap === sitemapModel ? currentExpandedAreaId : undefined
                }
                onExpand={(id) => {
                  setCurrentExpandedSitemap(sitemapModel)
                  toggleExpand(sitemapModel, id)
                }}
              />
            ))}
            {!shouldShowDefaultSitemap && !isKinexusSitemap && <DocumentDownloadSection />}
          </SidebarList>
          {shouldShowDefaultSitemap && <SidebarDivider className={className} />}
        </>
      )}

      {shouldShowDefaultSitemap && (
        <SidebarList component="nav">
          {defaultSitemap.areas.map((area, i) => (
            <ConditionalSitemapRenderer
              last={isKinexusSitemap ? i === defaultSitemap.areas.length - 1 : false}
              key={area.id}
              type={area.type}
              item={area}
              mode="Extended"
              selectedPathIds={selectedPathIds}
              level={0}
              currentExpanded={
                navigationMenuOpen && currentExpandedSitemap === defaultSitemap ? currentExpandedAreaId : undefined
              }
              onExpand={(id) => {
                setCurrentExpandedSitemap(defaultSitemap)
                toggleExpand(defaultSitemap, id)
              }}
            />
          ))}
          {!isKinexusSitemap && <DocumentDownloadSection />}
        </SidebarList>
      )}
      <DocumentSection />
    </Container>
  )
}
